import { FC, useContext, useEffect, useRef, useState } from 'react';

import { PassiveComponentContext } from '@pp/core/src/components/context';
import { useAudioTrigger } from '@pp/core/src/controllers/audioController/hooks';
import { CurrencyFormatter } from '@pp/core/src/formatters/CurrencyFormatter';
import { L10nText } from '@pp/core/src/services/uiTranslations/reactBindings/L10nText';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { useSettingsSlice } from '@pp/core/src/store/slices/settings/selectors';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import { winStates } from '../../store/slices/highflyerState/types';

import { useWinAmount } from './useWinAmount';

import styles from './WinResultConnected.module.css';

export const WinResultConnected: FC = () => {
  const [isWinDisplayed, setIsWinDisplayed] = useState(true);
  const [isYouWinDisplayed, setIsYouWinDisplayed] = useState(false);
  const { winState, amount: winAmount, playWinSound: winSound } = useWinAmount();
  const winTimeout = useRef(0);

  const audioTrigger = useAudioTrigger();
  const isPassive = useContext(PassiveComponentContext);

  const currency = useGameStateSlice.getCurrency() ?? '';
  const currentLanguage = useSettingsSlice.currentLanguage();
  const [integerWinAmt, decimalWinAmt] =
    winAmount < 1
      ? [CurrencyFormatter.format(winAmount, currentLanguage, { currency }), '']
      : CurrencyFormatter.splitFraction(winAmount, currentLanguage, {
          currency,
          avoidTrailingZero: winAmount % 1 === 0,
        });

  useEffect(() => {
    if (isPassive) return;
    if (winAmount) {
      setIsWinDisplayed(true);
      winSound && audioTrigger.play({ name: 'winSound' });
      clearTimeout(winTimeout.current);
      setIsYouWinDisplayed(false);
      winTimeout.current = window.setTimeout(() => {
        setIsWinDisplayed(false);
        setIsYouWinDisplayed(winStates.youWin === winState ? true : false);
      }, 3000);
    }
  }, [audioTrigger, isPassive, winAmount, winState]);

  const youWin_mods = useModifiers(styles, 'winResultContainer', {
    cashed: winStates.cashed === winState,
    win: winStates.youWin === winState,
  });

  if (winAmount <= 0 || !isWinDisplayed || isYouWinDisplayed) return null;
  return (
    <div className={styles.WinResultConnectedWrapper}>
      <div className={`${styles.winResultContainer} ${youWin_mods}`}>
        <div className={styles.winResultMessage}>
          <L10nText tk={winState === winStates.youWin ? 'YOU_WIN' : 'CASHED_CAPS'} />
        </div>
        <div className={styles.winResultAmount}>
          <div className={styles.amount}>
            <span className={styles.winResultIntAmount}>{integerWinAmt}</span>
            <span className={styles.winResultDecAmount}>{decimalWinAmt}</span>
          </div>
          <div className={styles.amountShadow}>
            <span className={styles.winResultIntAmount}>{integerWinAmt}</span>
            <span className={styles.winResultDecAmount}>{decimalWinAmt}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
